import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Heart, Share2 } from "lucide-react";
import { TRACKS, STREAMING } from "../data/content";
import { Waveform } from "./Waveform";
import { StreamLogo } from "./StreamLogo";

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export const Music = () => {
  const [currentId, setCurrentId] = useState(TRACKS[0].id);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [favs, setFavs] = useState({});
  const audioRef = useRef(null);

  const current = TRACKS.find((t) => t.id === currentId);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.src = current.src;
    a.load();
    setProgress(0);
    setDuration(0);
    if (playing) a.play().catch(() => setPlaying(false));
  }, [currentId]); // eslint-disable-line

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing]);

  const togglePlay = (id) => {
    if (id !== currentId) {
      setCurrentId(id);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * duration;
  };

  const toggleFav = (id) => setFavs((f) => ({ ...f, [id]: !f[id] }));

  const share = async (track) => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `it's Malie — ${track.title}`, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      /* no-op */
    }
  };

  return (
    <section
      id="music"
      data-testid="music-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505]"
    >
      <div
        className="spotlight"
        style={{
          width: 600,
          height: 600,
          background: "rgba(212,175,55,0.08)",
          top: "30%",
          right: "-200px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
        >
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-10 bg-[color:var(--gold)]" />
              <span className="overline">The Sound</span>
            </div>
            <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Press play. <span className="font-display italic gold-text">Feel it.</span>
            </h2>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={STREAMING.spotify}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stream-spotify"
                className="group inline-flex items-center gap-3 rounded-full border border-white/15 pl-2 pr-5 py-2 text-xs uppercase tracking-[0.18em] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-[color:var(--gold)] text-black grid place-items-center transition-transform group-hover:scale-110">
                  <StreamLogo name="spotify" size={14} />
                </span>
                Spotify
              </a>
              <a
                href={STREAMING.appleMusic}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stream-apple"
                className="group inline-flex items-center gap-3 rounded-full border border-white/15 pl-2 pr-5 py-2 text-xs uppercase tracking-[0.18em] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-[color:var(--gold)] text-black grid place-items-center transition-transform group-hover:scale-110">
                  <StreamLogo name="applemusic" size={14} />
                </span>
                Apple Music
              </a>
              <a
                href={STREAMING.tidal}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stream-tidal"
                className="group inline-flex items-center gap-3 rounded-full border border-white/15 pl-2 pr-5 py-2 text-xs uppercase tracking-[0.18em] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-[color:var(--gold)] text-black grid place-items-center transition-transform group-hover:scale-110">
                  <StreamLogo name="tidal" size={14} />
                </span>
                Tidal
              </a>
              <a
                href={STREAMING.youtubeMusic}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stream-youtube-music"
                className="group inline-flex items-center gap-3 rounded-full border border-white/15 pl-2 pr-5 py-2 text-xs uppercase tracking-[0.18em] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-[color:var(--gold)] text-black grid place-items-center transition-transform group-hover:scale-110">
                  <StreamLogo name="youtubemusic" size={14} />
                </span>
                YouTube Music
              </a>
            </div>
          </div>
        </motion.div>

        {/* Now playing card — wide banner above player */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="now-playing-card"
          className="glass-strong rounded-2xl overflow-hidden"
        >
          <div className="relative aspect-[21/9] sm:aspect-[21/8] w-full overflow-hidden border-b border-white/10">
            <img
              src={current.art}
              alt={current.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
          </div>
          <div className="p-6 sm:p-8 lg:p-10">
            <p className="overline mb-3">Now Playing</p>
            <h3 className="font-display text-3xl sm:text-5xl tracking-tight">{current.title}</h3>
            <p className="text-white/55 mt-2">{current.feat}</p>

            <div
              data-testid="progress-bar"
              onClick={onSeek}
              className="relative mt-6 h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer group"
            >
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[color:var(--amber)] to-[color:var(--gold)] rounded-full transition-all"
                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-white/50 font-mono">
              <span>{fmt(progress)}</span>
              <span>{fmt(duration) || current.duration}</span>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="main-play-pause"
                className="w-14 h-14 rounded-full bg-[color:var(--gold)] text-black grid place-items-center hover:scale-105 transition-transform"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
              <Waveform bars={28} className="h-7" />
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => toggleFav(current.id)} data-testid="fav-current" className={`w-11 h-11 rounded-full border border-white/15 grid place-items-center transition-colors ${favs[current.id] ? "text-[color:var(--gold)] border-[color:var(--gold)]" : "hover:text-[color:var(--gold)]"}`} aria-label="Favorite"><Heart size={16} fill={favs[current.id] ? "currentColor" : "none"} /></button>
                <button onClick={() => share(current)} data-testid="share-current" className="w-11 h-11 rounded-full border border-white/15 grid place-items-center hover:text-[color:var(--gold)] hover:border-[color:var(--gold)] transition-colors" aria-label="Share"><Share2 size={16} /></button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={() => share(current)} data-testid="share-track" className="btn-ghost text-xs"><Share2 size={14} /> Share Track</button>
            </div>
          </div>
        </motion.div>

        {/* Track list removed per request */}

        <audio
          ref={audioRef}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onEnded={() => setPlaying(false)}
          preload="metadata"
          data-testid="audio-element"
        />
      </div>
    </section>
  );
};
